<template>
	<div v-if="isloadedContent" ref="box" class="pdf-document">
		<div v-if="multipart && !singlePart">
			<dms-tabs :items="tabItems" :dms-tabs="activeTab" :extra-buttons="multipartButtons" :is-split-label-key="isSplitLabelKeyCmp">
				<template v-for="item in tabItems" #[item.id]>
					<pdf-page v-for="page in multipartPdfs[item.id].pages" :key="page.pageNumber" v-bind="{scale}" :page="page" />
				</template>
			</dms-tabs>
		</div>
		<pdf-page v-for="page in pages" v-else :ref="`page${page.pageNumber}`" :key="(page.pageNumber + actualWidth)" v-bind="{scale}" :page="page" />
	</div>
</template>

<script>
import Vue from 'vue';

import { PdfProvider } from '#services/pdfProvider';
import { parseMultipart, getBoundary } from '#services/multiPartProvider';

import PDFPage from './PDFPage.vue';
import { emailButton, printButton, saveButton } from '../../utils/tableOperations';

export default Vue.extend({
	components: {
		'pdf-page': PDFPage,
	},
	props: {
		url: String,
		filter: Object,
		fetchPdfProvider: Function,
		closeDialogAfterPrintGuard: Function,
		scale: { type: Number, default: 1.5 },
		loadingDomId: { type: String, default: 'pdfContent' },
		contentLoaded: { type: Boolean, default: false },
		multipart: { type: Boolean, default: false },
		isSplitLabelKey: { type: Boolean, default: false },
		isAnyError: { type: Boolean, default: false }
	},

	data() {
		return {
			isCloseDialogVisible: false,
			loadingInstance: null,
			pdf: undefined,
			pdfData: undefined,
			pages: [],
			pdfProvider: undefined,
			multipartPdfs: {},
			singlePart: false,
			tabItems: {},
			activeTab: [],
			isloadedContent: false,
			pdfProviders: [],
			actualWidth: 0,
		};
	},
	computed: {
		clientWidth() {
			if (this.$el && this.$el.parentElement) return this.$el.parentElement.clientWidth;
			return null;
		},
		multipartButtons() {
			return {
				...saveButton(this.saveItem),
				...printButton(this.printItem),
				...emailButton(this.emailItem),
			};
		},
		isSplitLabelKeyCmp() {
			return this.isSplitLabelKey;
		},
	},
	mounted() {
		// eslint-disable-next-line no-underscore-dangle
		const _this = this;
		const resizeObserver = new ResizeObserver(() => {
			_this.actualWidth = this.$parent.$parent.$el.clientWidth;
		});

		resizeObserver.observe(this.$parent.$parent.$el);
	},
	methods: {
		printItem(item) {
			const provider = this.getSelectedProvider(item.id);
			provider.api.print();
			this.$emit('onPrintDialogClosed');
			this.$emit('print-done');
		},
		saveItem(item) {
			const provider = this.getSelectedProvider(item.id);
			provider.api.save();
		},
		emailItem(item) {
			const { pdf } = this.multipartPdfs[item.id];
			this.$emit('emailPdf', pdf);
		},
		updateProvider(fetchProvider = this.fetchPdfProvider) {
			this.pdfProvider = new PdfProvider(this.url, fetchProvider);
		},
		async fetchPdf() {
			this.loadingInstance = this.$loading({ target: `#${this.loadingDomId}` });
			try {
				await this.updateProvider();
				this.pdf = await this.pdfProvider.fetch(this.filter);
				this.pages = await this.pdfProvider.pages();
				this.$emit('update:contentLoaded', true);
				return this.pdf;
			} catch {
				this.$emit('update:isAnyError', true);
				return null;
			} finally {
				this.stopLoading();
			}
		},
		async fetchMultipartPdf() {
			this.multipartPdfs = {};
			this.pdfProviders = [];
			this.loadingInstance = this.$loading({ target: `#${this.loadingDomId}` });
			try {
				const multipartFetch = await this.fetchPdfProvider();
				if (multipartFetch.data) {
					const multipartRawData = await multipartFetch.data.arrayBuffer();
					const boundary = getBoundary(multipartFetch.headers['content-type']);
					const parts = parseMultipart(Buffer.from(multipartRawData), boundary);
					parts.forEach((part) => {
						if (part.header.contentType === 'application/pdf') {
							const promise = Promise.resolve({ ...part, status: 200 });
							const name = part.header.name || '';
							this.tabItems[name] = { id: name, labelKey: name };

							this.pdfProviders.push({
								api: new PdfProvider(this.url, () => promise),
								header: part.header,
								tabItem: name,
							});
						}
					});
					this.singlePart = this.pdfProviders.length === 1;
					if (!this.singlePart) {
						// eslint-disable-next-line no-restricted-syntax
						for (const pdfProvider of this.pdfProviders) {
							// eslint-disable-next-line no-await-in-loop
							const pdf = await pdfProvider.api.fetch();
							// eslint-disable-next-line no-await-in-loop
							const pages = await pdfProvider.api.pages();
							this.$emit('update:contentLoaded', true);
							this.multipartPdfs[pdfProvider.tabItem] = { pages, pdf, header: pdfProvider.header, name: pdfProvider.tabItem };
						}
						return Object.values(this.multipartPdfs);
					}
					this.pdf = await this.pdfProviders[0].api.fetch();
					this.pages = await this.pdfProviders[0].api.pages();
					this.$emit('update:contentLoaded', true);
					return this.pdf;
				}
				return null;
			} catch {
				this.$emit('update:isAnyError', true);
				return null;
			} finally {
				this.stopLoading();
			}
		},
		getSelectedProvider(name) {
			return this.pdfProviders.find((provider) => name === provider.tabItem);
		},
		stopLoading() {
			this.isloadedContent = true;
			if (this.loadingInstance) {
				this.loadingInstance.close();
				this.loadingInstance = null;
			}
		},
		async closeDialogIfPossible() {
			if (this.closeDialogAfterPrintGuard) {
				this.isCloseDialogVisible = true;

				const canWeCloseDialogAfterPrint = await this.closeDialogAfterPrintGuard();
				if (canWeCloseDialogAfterPrint) {
					this.$emit('onPrintDialogClosed');
				}

				this.isCloseDialogVisible = false;
				return;
			}

			this.$emit('onPrintDialogClosed');
		},
		printPdf() {
			if (this.multipart) {
				if (this.pdfProviders.length > 0) {
					this.printPdfRecursive(0);
				}
			} else {
				this.$nextTick(() => {
					this.pdfProvider.print();
					this.closeDialogIfPossible();
				});
			}
			this.$emit('print-done');
		},
		printPdfRecursive(providerIndex) {
			const onPrintDialogClose = providerIndex + 1 === this.pdfProviders.length
				? () => { this.closeDialogIfPossible(); }
				: () => { this.printPdfRecursive(providerIndex + 1); };
			this.pdfProviders[providerIndex].api.print();
			onPrintDialogClose();
		},
		savePdf() {
			if (this.multipart) {
				this.pdfProviders.forEach((provider) => {
					provider.api.save();
				});
			} else {
				this.pdfProvider.save();
			}
			this.closeDialogIfPossible();
		},
		cleanCanvas() {
			this.pages = [];
			this.isloadedContent = false;
		},
	},
});
</script>
<style>
.pdf-document {
	position: fixed;
	overflow: scroll;
	width: 50vw;
	height: 80%;
}
</style>
